import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { VendingPackageVolumeByProductMonthlyListChart } from '@frontend/chart/components';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useInsightFetchRange } from '../../analytics/insights/insight-detail/use-insight-fetch-range';
import { getLast60DaysChartRange } from '../pudo/last-30-days-chart-range';
import { VendingProductPopularityItem, takeTopVendingProductPopularityItem, toVendingProductPopularityItems } from './vending-product-popularity';

export const useLast60DaysVendingProductPopularity = (accountId: string) => {
    const { formatMessage } = useIntl();
    const [items, setItems] = useState<VendingProductPopularityItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const unknownLabel = formatMessage({
        id: 'Objects.UNKNOWN.DEFAULT',
        description: 'Label for unknown',
        defaultMessage: 'Unknown'
    });

    const fetchRange = useInsightFetchRange(getLast60DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(accountId, VendingPackageVolumeByProductMonthlyListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams
        })
            .then((chart) => {
                if (!cancelled) {
                    setItems(toVendingProductPopularityItems(chart, unknownLabel));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, unknownLabel, fetchRange.key]);

    const highestRiser = useMemo(() => takeTopVendingProductPopularityItem(items, 'up'), [items]);
    const lowestFaller = useMemo(() => takeTopVendingProductPopularityItem(items, 'down'), [items]);

    return {
        items,
        highestRiser,
        lowestFaller,
        isLoading
    };
};
